import { TaxonomyItem } from '../../services/taxonomiesService';

/*
 * Suggests stakeholder categories from what a business card says (role, organization, department).
 * Used when the AI didn't return a category, or to offer alternatives next to its choice.
 * Works in English and French, with or without accents.
 */

export interface CategorySuggestion {
  option: TaxonomyItem;
  /** Higher is stronger; the AI's own pick scores 100 */
  score: number;
  source: 'card' | 'role' | 'organization';
}

export interface CardClues {
  category?: string | null;
  role?: string | null;
  department?: string | null;
  organization?: string | null;
}

const fold = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const normaliseKey = (value: string) => fold(value).trim().replace(/[\s_/]+/g, '-');

/** A concept: how to recognise it on a card, and how to recognise the matching category */
interface Concept {
  clues: RegExp;
  /** Matched against the category key and label */
  category: RegExp;
}

const CONCEPTS: Concept[] = [
  {
    clues: /\b(minist(er|ry|re|ere)|secretar(y|iat)[- ]general|secretaire general|permanent secretary|governor|gouverneur|prefe(c)?t|sous-prefet|commissioner|commissaire|government|gouvernement|agence nationale|national agency|directorate|direction nationale|directeur national|customs|douanes?|treasury|tresor|public service|fonction publique|cabinet|presidency|presidence|primature)\b/,
    category: /(government|gouvern|official|public)/,
  },
  {
    clues: /\b(ceo|pdg|founder|fondateur|co-?founder|managing director|chairman|chairwoman|owner|proprietaire|entrepreneur|business|company|compagnie|societe|sarl|s\.?a\.?|ltd|inc|llc|group(e)?|holding|bank|banque|consortium|industries|trading|commercial|sales manager|chief (executive|operating|financial) officer|cfo|coo)\b/,
    category: /(business|private|entrepreneur|company|corporate|sector-prive)/,
  },
  {
    clues: /\b(professor|professeur|lecturer|maitre de conferences|researcher|chercheur|university|universite|faculty|faculte|institute|institut|phd|doctorant|laborator(y|ie)|academ(y|ie|ic|ique)|school|ecole)\b/,
    category: /(academ|research|scien|university)/,
  },
  {
    clues: /\b(journalist(e)?|editor|redact(eur|rice)|reporter|correspondent|correspondant|media|medias|radio|television|tv|press|presse|newspaper|journal|blogger|blogueur|influencer|influenceur|communication officer|chargee? de communication)\b/,
    category: /(media|journalis|influenc|press)/,
  },
  {
    clues: /\b(ambassador|ambassadeur|ambassadrice|consul|consulate|consulat|embassy|ambassade|attache|charge d'affaires|diplomat(e|ic|ique)?)\b/,
    category: /(diplomat)/,
  },
  {
    clues: /\b(ngo|ong|association|foundation|fondation|activist|activiste|civil society|societe civile|network|reseau|collectif|coalition|advocacy|plaidoyer|volunteer|benevole)\b/,
    category: /(civil|ngo|cso|society)/,
  },
  {
    clues: /\b(mayor|maire|chief|chef (de quartier|de village|de canton|coutumier)|village|community|communaute|traditional|traditionnel|elder|sage|notable|quartier|district chief)\b/,
    category: /(community|communaut|tradition|local-authority|authority)/,
  },
  {
    clues: /\b(deputy|depute|member of parliament|\bmp\b|senator|senateur|political party|parti|politic(al|ian)|politique|councillor|conseiller national|national assembly|assemblee nationale|campaign|campagne|mouvement)\b/,
    category: /(politic)/,
  },
  {
    clues: /\b(imam|pastor|pasteur|bishop|eveque|archbishop|archeveque|priest|pretre|reverend|cardinal|mosque|mosquee|church|eglise|diocese|religious|religieux|clergy|clerge|mufti|ligue islamique)\b/,
    category: /(religio|faith|clergy)/,
  },
  {
    clues: /\b(colonel|lieutenant|captain|capitaine|army|armee|police|gendarmerie|military|militaire|commander|commandant|defen[cs]e|security|securite|forces armees|armed forces)\b/,
    category: /(security|securit|defen|military|militar|police)/,
  },
  {
    clues: /\b(undp|pnud|world bank|banque mondiale|usaid|giz|afd|unicef|unhcr|who|oms|fao|wfp|pam|european union|union europeenne|eu delegation|ifc|afdb|bad|development|developpement|cooperation|donor|bailleur|programme officer|united nations|nations unies)\b/,
    category: /(develop|donor|cooperat|international-agency|agency)/,
  },
];

const WEIGHT: Record<'role' | 'organization' | 'department', number> = { role: 3, department: 1.5, organization: 2 };

export function findCategory(options: TaxonomyItem[], value?: string | null): TaxonomyItem | undefined {
  if (!value || !value.trim()) return undefined;
  const wanted = normaliseKey(value);
  return options.find((o) => normaliseKey(o.key || '') === wanted || normaliseKey(o.label) === wanted);
}

export function suggestCategories(clues: CardClues, options: TaxonomyItem[], limit = 3): CategorySuggestion[] {
  if (!options.length) return [];
  const scores = new Map<string, CategorySuggestion>();
  const add = (option: TaxonomyItem, score: number, source: CategorySuggestion['source']) => {
    const current = scores.get(option.id);
    if (current) {
      current.score += score;
      if (score > 0 && current.source !== 'card' && source === 'role') current.source = 'role';
    } else {
      scores.set(option.id, { option, score, source });
    }
  };

  const fromCard = findCategory(options, clues.category);
  if (fromCard) add(fromCard, 100, 'card');

  (['role', 'department', 'organization'] as const).forEach((field) => {
    const text = clues[field];
    if (!text) return;
    const folded = ` ${fold(text)} `;
    for (const concept of CONCEPTS) {
      if (!concept.clues.test(folded)) continue;
      for (const o of options) {
        // Categories naming more facets of the concept fit better ("Media/Journalist" over "Influencer")
        const facets = new Set(
          Array.from(`${fold(o.key || '')} ${fold(o.label)}`.matchAll(new RegExp(concept.category.source, 'g'))).map((m) => m[0]),
        ).size;
        if (facets) add(o, WEIGHT[field] * (1 + 0.1 * (facets - 1)), field === 'department' ? 'role' : field);
      }
    }
  });

  return Array.from(scores.values())
    .sort((a, b) => b.score - a.score || a.option.label.localeCompare(b.option.label))
    .slice(0, limit);
}

/**
 * The category to pre-select: the AI's pick when valid, otherwise a clear heuristic winner
 * (a role clue, and ahead of the runner-up). Returns undefined when it's ambiguous.
 */
export function autoCategory(suggestions: CategorySuggestion[]): CategorySuggestion | undefined {
  const [top, next] = suggestions;
  if (!top) return undefined;
  if (top.source === 'card') return top;
  if (top.score >= 3 && (!next || top.score > next.score)) return top;
  return undefined;
}
